import { AnyRouter } from '@tanstack/react-router';

// Lazy load CustomerIO Analytics to avoid loading the SDK in gov/non-production environments
type CioAnalyticsModule = typeof import('./CioAnalytics').default;
let cioAnalyticsPromise: Promise<CioAnalyticsModule> | null = null;
const getCioAnalytics = () => {
  if (!cioAnalyticsPromise) {
    cioAnalyticsPromise = import('./CioAnalytics').then((m) => m.default);
  }
  return cioAnalyticsPromise;
};

const trackPageView = async (router: AnyRouter) => {
  const matches = router.state.matches;
  const pathname = router.state.location.pathname;
  const routeId = matches[matches.length - 1]?.routeId ?? pathname;
  try {
    const CioAnalytics = await getCioAnalytics();
    await CioAnalytics.trackPage(routeId, { path: pathname });
  } catch {
    // Clear the cached rejection so the next navigation retries the import
    // instead of permanently disabling page tracking. Note: for a failed
    // network fetch of the chunk (vs. a logic error), the browser's own
    // module cache may still short-circuit the retry until reload.
    cioAnalyticsPromise = null;
  }
};

/**
 * Subscribes to route resolutions and forwards them to CustomerIO as page
 * views. The SDK is initialized with `initialPageview: false`, so this is
 * the only source of page view events.
 *
 * Emits one page view for the current location immediately (the initial
 * navigation has already resolved by the time this is called, e.g. once
 * auth resolves), then one per subsequent navigation that changes the path.
 * Search-param-only updates (e.g. filters in the block builder) are skipped
 * via TanStack Router's own `pathChanged` flag on the `onResolved` event.
 *
 * @param router - The app's TanStack Router instance
 * @returns Unsubscribe function
 */
export const initPageTracking = (router: AnyRouter) => {
  trackPageView(router);

  return router.subscribe('onResolved', (event) => {
    if (!event.pathChanged) {
      return;
    }
    trackPageView(router);
  });
};
